if (!customElements.get('quick-add-bulk')) {
  const QUICK_ADD_BULK_DEBOUNCE_MS = 80;

  function getHeaderCartCount() {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return 0;

    const count = parseInt(bubble.dataset.cartCount, 10);
    if (Number.isFinite(count)) return count;

    const visibleCount = parseInt(bubble.querySelector('[aria-hidden="true"]')?.textContent, 10);
    return Number.isFinite(visibleCount) ? visibleCount : 0;
  }

  function renderHeaderCartCount(itemCount) {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;

    const safeCount = Math.max(0, parseInt(itemCount, 10) || 0);
    let bubble = cartLink.querySelector('.cart-count-bubble');

    if (safeCount === 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      cartLink.appendChild(bubble);
    }

    bubble.dataset.cartCount = String(safeCount);

    let visibleCount = bubble.querySelector('[aria-hidden="true"]');
    if (safeCount < 100) {
      if (!visibleCount) {
        visibleCount = document.createElement('span');
        visibleCount.setAttribute('aria-hidden', 'true');
        bubble.prepend(visibleCount);
      }
      visibleCount.textContent = String(safeCount);
    } else {
      visibleCount?.remove();
    }

    let accessibleCount = bubble.querySelector('.visually-hidden');
    if (!accessibleCount) {
      accessibleCount = document.createElement('span');
      accessibleCount.className = 'visually-hidden';
      bubble.appendChild(accessibleCount);
    }

    const currentLabel = accessibleCount.textContent.trim();
    const localizedNumber = /\d(?:[\d,.\u00a0\u202f ]*\d)?/;
    accessibleCount.textContent = localizedNumber.test(currentLabel)
      ? currentLabel.replace(localizedNumber, String(safeCount))
      : String(safeCount);
  }

  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');
        this.optimisticQuantities = new Map();

        this.addEventListener('change', this.onOptimisticQuantityChange.bind(this));

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, QUICK_ADD_BULK_DEBOUNCE_MS);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      onOptimisticQuantityChange(event) {
        const input = event.target.closest?.('input.quantity__input');
        if (!input || !this.contains(input)) return;

        const nextQuantity = parseInt(input.value, 10);
        const minimum = parseInt(input.dataset.min, 10) || 1;
        const step = parseInt(input.step, 10) || 1;
        const maximum = input.max ? parseInt(input.max, 10) : null;
        const isValid =
          nextQuantity === 0 ||
          (Number.isFinite(nextQuantity) &&
            nextQuantity >= minimum &&
            (maximum === null || nextQuantity <= maximum) &&
            nextQuantity % step === 0);

        if (!isValid) return;

        const id = input.dataset.index;
        const storedQuantity = this.optimisticQuantities.get(id);
        const previousQuantity = Number.isFinite(storedQuantity)
          ? storedQuantity
          : parseInt(input.getAttribute('value') || input.dataset.cartQuantity, 10) || 0;

        if (nextQuantity === previousQuantity) return;

        this.optimisticQuantities.set(id, nextQuantity);
        renderHeaderCartCount(getHeaderCartCount() + nextQuantity - previousQuantity);
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');

        const ids = Object.keys(items);
        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then(async (response) => {
            const parsedState = await response.json();
            if (!response.ok || parsedState.status) {
              throw new Error(parsedState.description || parsedState.message || window.cartStrings.error);
            }
            return parsedState;
          })
          .then((parsedState) => {
            this.renderSections(parsedState, ids);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch((error) => {
            console.error(error);
            this.reconcileCartState(ids);
            // Commented out for now and will be fixed when BE issue is done https://github.com/Shopify/shopify/issues/440605
            // e.target.setCustomValidity(error);
            // e.target.reportValidity();
            // this.resetQuantityInput(ids[index]);
            // this.selectProgressBar().classList.add('hidden');
            // e.target.select();
            // this.cleanErrorMessageOnType(e);
          })
          .finally(() => {
            this.selectProgressBar().classList.add('hidden');
            this.setRequestStarted(false);
          });
      }

      getSectionsToRender() {
        return [
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;

        this.syncConfirmedQuantities(parsedState, ids);
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (section.section === 'cart-drawer') {
            sectionElement?.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.items.length === 0);
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }

      syncConfirmedQuantities(cartState, ids) {
        ids.forEach((id) => {
          if (this.queue.some((item) => String(item.id) === String(id))) return;

          const confirmedQuantity = (cartState.items || []).reduce((total, item) => {
            const variantId = item.variant_id || item.id;
            return String(variantId) === String(id) ? total + item.quantity : total;
          }, 0);
          const input = this.querySelector(`#Quantity-${id}`);
          if (!input) return;

          input.value = confirmedQuantity;
          input.setAttribute('value', confirmedQuantity);
          input.dataset.cartQuantity = confirmedQuantity;
          this.optimisticQuantities.delete(String(id));
        });
      }

      async reconcileCartState(ids) {
        try {
          const response = await fetch(`${routes.cart_url}.js`, {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) return;

          const cartState = await response.json();
          renderHeaderCartCount(cartState.item_count);
          this.syncConfirmedQuantities(cartState, ids);
        } catch (error) {
          console.error(error);
        }
      }
    }
  );
}
